import React from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@swyftx/aviary/atoms/Card';
import { Notification } from '@swyftx/aviary/atoms/Notification';
import { Skeleton } from '@swyftx/aviary/atoms/Skeleton';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { Filter } from '@swyftx/aviary/icons/outlined';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';
import { SingleLoanHealthCard } from '@swyftx/features/loans/components';
import { LoanSummaryCard } from '@swyftx/features/loans/components';

import { AppFeature, useIsFeatureEnabled } from 'src/config';
import { ApplicationStatusCard } from 'src/features/loans/dashboard/ApplicationStatusCard';
import { useLoansDashboard } from 'src/features/loans/hooks/useLoansDashboard';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

// TODO: this dashboard assumes there is only one application and one loan per user
const LoansDashboard: React.FC = () => {
  const { navigate } = useNavigateRoute();
  const { isFeatureEnabled } = useIsFeatureEnabled();
  const loansDashboardEnabled = isFeatureEnabled(AppFeature.Loans);

  const {
    loans,
    applications,
    applicationsRest: { isLoading: isApplicationsLoading, isFetching: isApplicationsFetching },
    loansRest: { isLoading: isLoansLoading },
  } = useLoansDashboard();

  if (!loansDashboardEnabled) {
    navigate(NavigationURLs.Loans);
    return null;
  }

  if (!isApplicationsLoading && (!applications || applications.length === 0)) {
    navigate(NavigationURLs.Loans);
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const LoanClosed = () => (
    <Notification title='You closed this loan' severity='destructive' className='mb-16'>
      <Body>
        This loan has been repaid and is now closed. You can continue to view this loan&apos;s history here, or apply
        for a new loan.
      </Body>
      <div className='mt-12 flex gap-2'>
        <Button size='sm' onClick={() => navigate(NavigationURLs.LoansApplication)}>
          Apply for a new loan
        </Button>
      </div>
    </Notification>
  );

  return (
    <div className='min-h-screen px-16 py-32 md:px-64'>
      <div className='mb-32'>
        <div className='flex flex-col gap-24 md:flex-row md:items-center md:justify-between'>
          <div className='mb-4 md:mb-0'>
            <Skeleton loading={isApplicationsLoading}>
              <EnhancedTooltip
                name='Total accrued interest'
                title='The total amount currently owed on the loan, including outstanding principal, origination fee, and any
                  accrued interest.'
                align='start'
                side='top'
              >
                <Body color='secondary' underline='dashed' className='mb-12'>
                  Total outstanding balance
                </Body>
              </EnhancedTooltip>
            </Skeleton>

            <Skeleton loading={isApplicationsLoading}>
              <Numeric size='5xl' weight='bold'>
                ${loans?.[0].borrowPrincipalAmount?.toFixed(2) ?? '0.00'} {loans?.[0].borrowAssetCode}
              </Numeric>
            </Skeleton>
          </div>

          <div className='flex flex-col gap-24 md:flex-row md:gap-12'>
            <div className='flex gap-24 md:gap-12'>
              <Button variant='filled' className='w-full font-semibold'>
                Make a repayment
              </Button>
              <Button variant='outlined' className='w-full font-semibold'>
                Request a redraw
              </Button>
            </div>
            <Button variant='outlined' className='w-full font-semibold md:w-auto'>
              ⋯ <span className='inline md:hidden'>More options</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Application Status */}
      <ApplicationStatusCard application={applications?.[0]} isFetchingApplications={isApplicationsFetching} />

      {/* Main Content Grid */}
      <div className='mb-8 grid grid-cols-1 gap-8 lg:grid-cols-2'>
        <SingleLoanHealthCard loanData={loans?.[0]} isFetchingLoan={false} />
        <LoanSummaryCard loanData={loans?.[0]} isFetchingLoan={isLoansLoading} />
      </div>

      {/* History Section */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle className='text-lg'>History</CardTitle>
            <Button variant='outlined' size='sm' className='flex items-center gap-2 bg-transparent'>
              <Filter className='h-4 w-4' />
              Filters
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className='py-12 text-center'>
            <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100'>
              <div className='flex h-8 w-8 items-center justify-center rounded-full bg-blue-600'>
                <span className='text-sm text-white'>📊</span>
              </div>
            </div>
            <h3 className='mb-2 text-lg font-semibold'>No loan history</h3>
            <p className='text-sm'>Loan history will appear here once transactions exist on this loan.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { LoansDashboard };
